import { useEffect } from "react";

import { isLoggedIn, Spinner } from "@shapr3d/frontend-lib";
import { useParams, useSearchParams } from "react-router-dom";

import { DeepLinkModal } from "../../components/DeepLinkModal/DeepLinkModal";
import ErrorPage from "../../components/ErrorPage/ErrorPage";
import { OverlayProvider } from "../../components/Headers/components/ShareModal/components/MenuPortal";
import { GenericHeader } from "../../components/Headers/GenericHeader/GenericHeader";
import { TeamsHeader } from "../../components/Headers/TeamsHeader/TeamsHeader";
import { ProjectMetadata } from "../../components/ProjectMetadata/ProjectMetadata";
import { ProjectPageHeader } from "../../components/ProjectPageHeader/ProjectPageHeader";
import { ViewLinkList } from "../../components/ViewLinkList/ViewLinkList";
import useAccount from "../../hooks/useAccount";
import { useHandleProjectInvitation } from "../../hooks/useHandleInvitation";
import { ProjectUserRole, useProject } from "../../hooks/useProject";
import { SearchParam } from "../../types";
import { navigateToAuth } from "../../utils/authUtils";
import useTracker from "../../utils/useTracker";
import NotFoundPage from "../NotFoundPage/NotFoundPage";
import type { ProjectViewLink } from "../ViewLinkListPage/ViewLinkListPage";

import style from "./ProjectPage.module.css";

const ProjectPage = () => {
  const { projectId } = useParams();
  const tracker = useTracker();
  const [searchParams] = useSearchParams();
  const hasInvitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  const { isError: isInvitationError } = useHandleProjectInvitation();
  const { data: accountData } = useAccount();
  const { data, isPending, isError, isDownloadFileEnabled } = useProject(
    Boolean(hasInvitationHash)
  );

  useEffect(() => {
    if (isError) {
      tracker.trackProjectPageLoaded({
        notFound: true,
        projectId: projectId ?? "",
      });
    }
  }, [tracker, isError, projectId]);

  useEffect(() => {
    if (data) {
      tracker.trackProjectPageLoaded({
        notFound: false,
        projectId: data.project.projectId,
        projectTeamId: data.teamId,
      });
    }
  }, [data, tracker]);

  if (!isLoggedIn() && !hasInvitationHash) {
    navigateToAuth();
    return;
  }

  if (isInvitationError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <ErrorPage />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <NotFoundPage isProjectPage />
      </div>
    );
  }

  if (isPending || hasInvitationHash) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <div className={style.centerContent}>
          <Spinner />
        </div>
      </div>
    );
  }

  const projectViewLinks: ProjectViewLink[] =
    data.projectViewLinks?.map((viewLink) => ({
      title: data.project.name,
      projectViewLinkId: viewLink.projectViewLinkId,
      space: {
        id: "",
        spaceName: data.space?.spaceName,
        spaceType: "team",
      },
      uploadedAt: viewLink.updatedAt,
      uploadedByUserId: viewLink.uploadedByUserId ?? "",
    })) || [];

  return (
    <OverlayProvider>
      <TeamsHeader teamId={accountData?.teams[0].id} />
      <div className={style.container}>
        <ProjectPageHeader projectData={data} isDownloadEnabled={isDownloadFileEnabled} />
        <div className={style.internalContainer}>
          <div className={style.sideContent}>
            {data.latestRevision && <ProjectMetadata projectData={data} />}
          </div>
          <div className={style.mainContent}>
            <div className={style.title}>Review link</div>
            <ViewLinkList
              viewLinks={projectViewLinks}
              users={data.users}
              isShortVersion={true}
              isUnpublishHidden={data.userRole !== ProjectUserRole.Owner}
            />
          </div>
        </div>
      </div>
      <DeepLinkModal />
    </OverlayProvider>
  );
};

export default ProjectPage;
